"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusGUIManager = exports.BaseComponent = void 0;
/**
 * 基础组件类 - 所有UI组件的基类
 * 提供通用的属性和方法
 */
class BaseComponent {
    constructor(container) {
        this._enabled = true;
        this._visible = true;
        this._id = `component_${BaseComponent._componentCounter++}`;
        this.container = container || this.getDefaultContainer();
        this.createElement();
        this.render();
        this.bindEvents();
    }
    /**
     * 获取组件的唯一ID
     */
    get id() {
        return this._id;
    }
    /**
     * 获取/设置组件是否启用
     */
    get enabled() {
        return this._enabled;
    }
    set enabled(value) {
        this._enabled = value;
        this.updateEnabledState();
    }
    /**
     * 获取/设置组件是否可见
     */
    get visible() {
        return this._visible;
    }
    set visible(value) {
        this._visible = value;
        this.updateVisibleState();
    }
    /**
     * 获取组件的DOM元素
     */
    get domElement() {
        return this.element;
    }
    /**
     * 绑定事件处理器 - 子类可以重写
     */
    bindEvents() {
        // 默认实现为空，子类可以重写
    }
    /**
     * 更新启用状态
     */
    updateEnabledState() {
        if (this.element) {
            if (this._enabled) {
                this.element.removeAttribute('disabled');
                this.element.classList.remove('disabled');
            }
            else {
                this.element.setAttribute('disabled', 'true');
                this.element.classList.add('disabled');
            }
        }
    }
    /**
     * 更新可见状态
     */
    updateVisibleState() {
        if (this.element) {
            this.element.style.display = this._visible ? '' : 'none';
        }
    }
    /**
     * 获取默认容器
     */
    getDefaultContainer() {
        return NexusGUIManager.getDefaultContainer();
    }
    // === 公共样式API ===
    /**
     * 添加CSS类
     */
    addClass(className) {
        if (this.element) {
            this.element.classList.add(className);
        }
        return this;
    }
    /**
     * 移除CSS类
     */
    removeClass(className) {
        if (this.element) {
            this.element.classList.remove(className);
        }
        return this;
    }
    /**
     * 切换CSS类
     */
    toggleClass(className) {
        if (this.element) {
            this.element.classList.toggle(className);
        }
        return this;
    }
    /**
     * 检查是否有指定的CSS类
     */
    hasClass(className) {
        return this.element ? this.element.classList.contains(className) : false;
    }
    /**
     * 设置单个样式属性
     */
    setStyle(property, value) {
        if (this.element) {
            this.element.style.setProperty(property, value);
        }
        return this;
    }
    /**
     * 设置多个样式属性
     */
    setStyles(styles) {
        if (this.element) {
            Object.keys(styles).forEach(property => {
                this.element.style.setProperty(property, styles[property]);
            });
        }
        return this;
    }
    /**
     * 设置背景色
     */
    setBackgroundColor(color) {
        return this.setStyle('background-color', color);
    }
    /**
     * 设置文本颜色
     */
    setTextColor(color) {
        return this.setStyle('color', color);
    }
    /**
     * 设置字体大小
     */
    setFontSize(size) {
        return this.setStyle('font-size', size);
    }
    /**
     * 设置字体族
     */
    setFontFamily(family) {
        return this.setStyle('font-family', family);
    }
    /**
     * 设置字体权重
     */
    setFontWeight(weight) {
        return this.setStyle('font-weight', weight);
    }
    /**
     * 设置内边距
     */
    setPadding(padding) {
        return this.setStyle('padding', padding);
    }
    /**
     * 设置外边距
     */
    setMargin(margin) {
        return this.setStyle('margin', margin);
    }
    /**
     * 设置边框
     */
    setBorder(border) {
        return this.setStyle('border', border);
    }
    /**
     * 设置边框圆角
     */
    setBorderRadius(radius) {
        return this.setStyle('border-radius', radius);
    }
    /**
     * 设置宽度
     */
    setWidth(width) {
        return this.setStyle('width', width);
    }
    /**
     * 设置高度
     */
    setHeight(height) {
        return this.setStyle('height', height);
    }
    /**
     * 设置透明度
     */
    setOpacity(opacity) {
        return this.setStyle('opacity', opacity);
    }
    /**
     * 设置阴影
     */
    setBoxShadow(shadow) {
        return this.setStyle('box-shadow', shadow);
    }
    /**
     * 设置文本对齐
     */
    setTextAlign(align) {
        return this.setStyle('text-align', align);
    }
    /**
     * 应用预定义主题
     */
    applyTheme(theme) {
        // 移除现有主题类
        this.removeClass('theme-default')
            .removeClass('theme-primary')
            .removeClass('theme-success')
            .removeClass('theme-warning')
            .removeClass('theme-danger')
            .removeClass('theme-info');
        // 添加新主题类
        this.addClass(`theme-${theme}`);
        return this;
    }
    /**
     * 销毁组件
     */
    destroy() {
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
        NexusGUIManager.unregisterComponent(this);
    }
    /**
     * 刷新组件
     */
    refresh() {
        this.render();
    }
}
exports.BaseComponent = BaseComponent;
BaseComponent._componentCounter = 0;
/**
 * GUI管理器 - 管理全局状态和容器
 */
class NexusGUIManager {
    /**
     * 初始化GUI管理器
     */
    static initialize(container) {
        if (!this._initialized) {
            // 自动加载样式
            this.loadStyles();
            this._defaultContainer = container || this.createDefaultContainer();
            this._initialized = true;
        }
    }
    /**
     * 获取默认容器
     */
    static getDefaultContainer() {
        if (!this._initialized) {
            this.initialize();
        }
        return this._defaultContainer;
    }
    /**
     * 设置默认容器
     */
    static setDefaultContainer(container) {
        this._defaultContainer = container;
        this._initialized = true;
    }
    /**
     * 注册组件
     */
    static registerComponent(component) {
        this._components.set(component.id, component);
    }
    /**
     * 注销组件
     */
    static unregisterComponent(component) {
        this._components.delete(component.id);
    }
    /**
     * 获取组件
     */
    static getComponent(id) {
        return this._components.get(id);
    }
    /**
     * 获取所有组件
     */
    static getAllComponents() {
        return Array.from(this._components.values());
    }
    /**
     * 清理所有组件
     */
    static clear() {
        this._components.forEach(component => component.destroy());
        this._components.clear();
    }
    /**
     * 创建默认容器
     */
    static createDefaultContainer() {
        const container = document.createElement('div');
        container.className = 'nexus-gui-container';
        document.body.appendChild(container);
        return container;
    }
    /**
     * 加载样式
     */
    static loadStyles() {
        // 简单的样式加载，避免循环引用
        if (document.getElementById('nexus-gui-styles')) {
            return;
        }
        const styleElement = document.createElement('style');
        styleElement.id = 'nexus-gui-styles';
        styleElement.textContent = this.getDefaultStyles();
        document.head.appendChild(styleElement);
    }
    /**
     * 获取默认样式
     */
    static getDefaultStyles() {
        return `
/* NexusGUI 基础样式 */
.nexus-gui-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    background-color: #fafafa;
    padding: 16px;
    box-sizing: border-box;
}

.nexus-textfield {
    margin-bottom: 12px;
}

.nexus-textfield-label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.nexus-textfield-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s ease;
    background-color: #ffffff;
}

.nexus-textfield-input:focus {
    border-color: #007acc;
    box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.1);
}

.nexus-toggle {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.nexus-toggle-input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #007acc;
}

.nexus-toggle-label {
    font-size: 14px;
    color: #333;
    cursor: pointer;
    user-select: none;
}

.nexus-dropdown {
    margin-bottom: 12px;
}

.nexus-dropdown-label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.nexus-dropdown-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    font-size: 14px;
    outline: none;
    background-color: #ffffff;
    cursor: pointer;
    transition: border-color 0.2s ease;
}

.nexus-dropdown-select:focus {
    border-color: #007acc;
    box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.1);
}

.nexus-label {
    color: #333;
    font-size: 14px;
    margin-bottom: 8px;
}

.nexus-separator {
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 16px 0;
    height: 0;
}

.nexus-infobox {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 12px;
    font-size: 14px;
    border: 1px solid;
}

.nexus-infobox-icon {
    font-size: 16px;
    font-weight: bold;
    flex-shrink: 0;
}

.nexus-infobox-text {
    flex: 1;
}

.nexus-infobox-info {
    background-color: #e6f3ff;
    border-color: #b3d9ff;
    color: #004085;
}

.nexus-infobox-warning {
    background-color: #fff3cd;
    border-color: #ffeaa7;
    color: #856404;
}

.nexus-infobox-error {
    background-color: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
}

.nexus-infobox-success {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
}

.nexus-button {
    background-color: #007acc;
    color: #ffffff;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    margin-right: 8px;
    margin-bottom: 8px;
}

.nexus-button:hover {
    background-color: #005a9e;
}

.nexus-button:disabled {
    background-color: #ccc;
    color: #999;
    cursor: not-allowed;
}

.nexus-group {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    margin-bottom: 16px;
    background-color: #ffffff;
    overflow: hidden;
}

.nexus-group-title {
    background-color: #f8f9fa;
    padding: 12px 16px;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #e0e0e0;
}

.nexus-group-content {
    padding: 16px;
}

.nexus-horizontal {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.nexus-horizontal > * {
    margin-bottom: 0;
}

.disabled {
    opacity: 0.6;
    pointer-events: none;
}

/* 主题样式 */
.theme-primary {
    --accent-color: #007acc;
    --accent-hover: #005a9e;
    --accent-light: #e6f3ff;
    --accent-border: #b3d9ff;
    --accent-text: #004085;
}

.theme-success {
    --accent-color: #28a745;
    --accent-hover: #218838;
    --accent-light: #d4edda;
    --accent-border: #c3e6cb;
    --accent-text: #155724;
}

.theme-warning {
    --accent-color: #ffc107;
    --accent-hover: #e0a800;
    --accent-light: #fff3cd;
    --accent-border: #ffeaa7;
    --accent-text: #856404;
}

.theme-danger {
    --accent-color: #dc3545;
    --accent-hover: #c82333;
    --accent-light: #f8d7da;
    --accent-border: #f5c6cb;
    --accent-text: #721c24;
}

.theme-info {
    --accent-color: #17a2b8;
    --accent-hover: #138496;
    --accent-light: #d1ecf1;
    --accent-border: #b6d7dd;
    --accent-text: #0c5460;
}

.theme-default {
    --accent-color: #6c757d;
    --accent-hover: #5a6268;
    --accent-light: #e2e3e5;
    --accent-border: #d6d8db;
    --accent-text: #383d41;
}

/* 主题按钮样式 */
.theme-primary .nexus-button,
.theme-success .nexus-button,
.theme-warning .nexus-button,
.theme-danger .nexus-button,
.theme-info .nexus-button,
.theme-default .nexus-button {
    background-color: var(--accent-color);
    color: #ffffff;
}

.theme-primary .nexus-button:hover,
.theme-success .nexus-button:hover,
.theme-warning .nexus-button:hover,
.theme-danger .nexus-button:hover,
.theme-info .nexus-button:hover,
.theme-default .nexus-button:hover {
    background-color: var(--accent-hover);
}

/* 主题焦点样式 */
.theme-primary .nexus-textfield-input:focus,
.theme-primary .nexus-dropdown-select:focus,
.theme-success .nexus-textfield-input:focus,
.theme-success .nexus-dropdown-select:focus,
.theme-warning .nexus-textfield-input:focus,
.theme-warning .nexus-dropdown-select:focus,
.theme-danger .nexus-textfield-input:focus,
.theme-danger .nexus-dropdown-select:focus,
.theme-info .nexus-textfield-input:focus,
.theme-info .nexus-dropdown-select:focus,
.theme-default .nexus-textfield-input:focus,
.theme-default .nexus-dropdown-select:focus {
    border-color: var(--accent-color);
    box-shadow: 0 0 0 2px rgba(var(--accent-color), 0.1);
}

/* 主题信息框样式 */
.theme-primary .nexus-infobox-info,
.theme-success .nexus-infobox-success,
.theme-warning .nexus-infobox-warning,
.theme-danger .nexus-infobox-error,
.theme-info .nexus-infobox-info,
.theme-default .nexus-infobox {
    background-color: var(--accent-light);
    border-color: var(--accent-border);
    color: var(--accent-text);
}

/* 暗黑主题支持 */
@media (prefers-color-scheme: dark) {
    .nexus-gui-container {
        background-color: #1e1e1e;
        color: #ffffff;
    }
    
    .nexus-textfield-input,
    .nexus-dropdown-select {
        background-color: #2d2d2d;
        border-color: #555;
        color: #ffffff;
    }
    
    .nexus-group {
        background-color: #2d2d2d;
        border-color: #555;
    }
    
    .nexus-group-title {
        background-color: #3d3d3d;
        color: #ffffff;
        border-bottom-color: #555;
    }
    
    .nexus-textfield-label,
    .nexus-dropdown-label,
    .nexus-toggle-label,
    .nexus-label {
        color: #ffffff;
    }
    
    .nexus-separator {
        border-top-color: #555;
    }
}
        `;
    }
}
exports.NexusGUIManager = NexusGUIManager;
NexusGUIManager._components = new Map();
NexusGUIManager._initialized = false;
